import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft, Loader2, Smartphone, Tablet, RefreshCw, Activity, UploadCloud,
  DownloadCloud, AlertTriangle, Clock, Gauge,
} from "lucide-react";
import { api } from "@/api/client";
import type { SyncSession, DeviceSummary } from "@/api/types";

function fmt(dt?: string | null) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString("fr-FR"); } catch { return dt; }
}
function ago(dt?: string | null) {
  if (!dt) return "";
  const d = (Date.now() - new Date(dt).getTime()) / 1000;
  if (d < 60) return "il y a quelques secondes";
  if (d < 3600) return `il y a ${Math.floor(d / 60)} min`;
  if (d < 86400) return `il y a ${Math.floor(d / 3600)} h`;
  return `il y a ${Math.floor(d / 86400)} j`;
}

export default function DiagnosticsPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: summary, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["sync-summary"],
    queryFn: api.syncSummary,
    refetchInterval: 15000,
  });
  const { data: sessions = [] } = useQuery<SyncSession[]>({
    queryKey: ["sync-sessions", selected],
    queryFn: () => api.syncSessions(selected || undefined, 100),
  });

  const devices: DeviceSummary[] = summary?.devices || [];

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Tableau de bord
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Diagnostic de synchronisation</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Historique horodaté des sessions <span className="font-mono">/api/sync</span> par appareil — {summary?.total_sessions ?? 0} session(s).
          </p>
        </div>
        <button
          data-testid="refresh-diagnostics-button"
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : devices.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-20 text-center">
          <Activity className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">Aucune session de synchronisation enregistrée pour l'instant.</p>
          <p className="mt-1 uppercase-label text-muted-foreground">Les appareils mobiles apparaîtront ici après leur premier appel /api/sync.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {devices.map((d) => {
              const isTablet = /ipad|tablet/i.test(d.device_info?.model || d.device_id);
              const active = selected === d.device_id;
              return (
                <button
                  key={d.device_id}
                  data-testid={`device-card-${d.device_id}`}
                  onClick={() => setSelected(active ? null : d.device_id)}
                  className={`rounded-xl border p-5 text-left transition-all ${active ? "border-primary/60 bg-[#1A2234]" : "border-border/60 bg-card hover:border-primary/40"}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 border border-primary/20">
                      {isTablet ? <Tablet className="h-5 w-5 text-primary" /> : <Smartphone className="h-5 w-5 text-primary" />}
                    </div>
                    {d.total_conflicts > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[11px] font-mono text-rose-400">
                        <AlertTriangle className="h-3 w-3" /> {d.total_conflicts} conflit(s)
                      </span>
                    )}
                  </div>
                  <h3 className="mt-4 font-mono text-lg font-semibold">{d.device_info?.model || d.device_id}</h3>
                  <p className="uppercase-label text-muted-foreground">
                    {d.device_info?.brand ? `${d.device_info.brand} · ` : ""}{d.device_id}
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div><div className="font-mono text-xl">{d.sessions}</div><div className="uppercase-label text-muted-foreground">sessions</div></div>
                    <div><div className="font-mono text-xl text-primary">#{d.last_cursor}</div><div className="uppercase-label text-muted-foreground">curseur</div></div>
                    <div><div className="font-mono text-xl text-emerald-400">{d.total_pushed}</div><div className="uppercase-label text-muted-foreground">poussés</div></div>
                    <div><div className="font-mono text-xl">{d.avg_duration_ms}ms</div><div className="uppercase-label text-muted-foreground">durée moy.</div></div>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 uppercase-label text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> dernière sync {ago(d.last_sync)}
                  </div>
                </button>
              );
            })}
          </div>

          <div>
            <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
              <Activity className="h-5 w-5 text-primary" />
              Journal des sessions {selected && <span className="font-mono text-sm text-muted-foreground">— {selected}</span>}
            </h2>
            <div data-testid="sync-session-list" className="space-y-2">
              {sessions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">Aucune session.</div>
              ) : sessions.map((s) => (
                <div key={s.id} data-testid={`sync-session-${s.id}`} className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary">
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="font-mono text-sm">{fmt(s.created_at)}</div>
                      <div className="uppercase-label text-muted-foreground">{s.device_id} · since #{s.since} → curseur #{s.cursor}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
                    <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-400">
                      <UploadCloud className="h-3.5 w-3.5" /> {s.push.applied} appl.
                    </span>
                    {s.push.skipped_older > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-amber-400">{s.push.skipped_older} ignorés</span>
                    )}
                    {s.push.conflict > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-rose-400">
                        <AlertTriangle className="h-3.5 w-3.5" /> {s.push.conflict} conflits
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-cyan-400">
                      <DownloadCloud className="h-3.5 w-3.5" /> {s.pull.total} tirés
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-muted-foreground">
                      <Gauge className="h-3.5 w-3.5" /> {s.duration_ms}ms
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
